/**
 * Find placement for a new object: center first, then scan for empty space if occupied.
 * @returns {{ left: number, top: number }} center point for addImage/addTextBox-style APIs
 */
export function findOpenPlacement(scene, width, height, preferredCenter = null) {
  const pageW = Math.max(1, Number(scene?.width) || 800);
  const pageH = Math.max(1, Number(scene?.height) || 600);
  const w = Math.max(8, Number(width) || 100);
  const h = Math.max(8, Number(height) || 80);
  const margin = 16;
  const gap = 10;
  const objects = scene?.objects || [];

  const overlaps = (x, y) => {
    for (const o of objects) {
      const t = o?.transform;
      if (!t) continue;
      const ox = Number(t.x) || 0;
      const oy = Number(t.y) || 0;
      const ow = Math.max(1, Number(t.width) || 100);
      const oh = Math.max(1, Number(t.height) || 40);
      if (x + w + gap > ox && x < ox + ow + gap && y + h + gap > oy && y < oy + oh + gap) {
        return true;
      }
    }
    return false;
  };

  const toCenter = (x, y) => ({ left: x + w / 2, top: y + h / 2 });

  const tryPoint = (cx, cy) => {
    const x = cx - w / 2;
    const y = cy - h / 2;
    if (x >= margin && y >= margin && x + w <= pageW - margin && y + h <= pageH - margin && !overlaps(x, y)) {
      return { left: cx, top: cy };
    }
    return null;
  };

  if (preferredCenter) {
    const px = Number(preferredCenter.left ?? preferredCenter.x) || pageW / 2;
    const py = Number(preferredCenter.top ?? preferredCenter.y) || pageH / 2;
    const hit = tryPoint(px, py);
    if (hit) return hit;
  } else {
    const hit = tryPoint(pageW / 2, pageH / 2);
    if (hit) return hit;
  }

  const step = Math.max(20, Math.round(Math.min(w, h) / 2));
  for (let y = margin; y + h <= pageH - margin; y += step) {
    for (let x = margin; x + w <= pageW - margin; x += step) {
      if (!overlaps(x, y)) return toCenter(x, y);
    }
  }

  return { left: pageW / 2, top: pageH / 2 };
}
